import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Check, X } from "lucide-react";

import auth from "../auth/AuthLayout.module.css";

import { readDoneState } from "./doneState";
import styles from "./AuthorizePage.module.css";

// A top-level navigation, not fetch or an iframe: Chrome's local-network-access
// rules block a public origin from reaching the client's loopback any other way.
function handToClient(url: string) {
  window.location.assign(url);
}

interface Props {
  /** Leaves the admin for the client's redirect URL; replaced in tests. */
  assign?: (url: string) => void;
}

export function AuthorizeDonePage({ assign = handToClient }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  // A local copy, because the router state it came from is wiped below and
  // the screen has to outlive it.
  const [state] = useState(() => readDoneState(location.state));
  const stateInHistory = location.state != null;
  const handedOver = useRef(false);

  // The redirect carries a single-use code, so it leaves the history entry
  // first: a reload or a Back onto this page then has nothing to replay.
  useEffect(() => {
    if (state && stateInHistory) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [state, stateInHistory, navigate, location.pathname]);

  // Only once the entry is clean, and once per mount even under StrictMode.
  useEffect(() => {
    if (!state || stateInHistory || handedOver.current) return;
    handedOver.current = true;
    assign(state.redirectUrl);
  }, [state, stateInHistory, assign]);

  if (!state) return <Navigate to="/projects" replace />;

  if (state.kind === "cancelled") {
    return (
      <div className={auth.page}>
        <div className={`${auth.card} ${auth.hero}`}>
          <div className={`${auth.heroIcon} ${auth.heroIconNeutral}`}>
            <X size={30} aria-hidden="true" />
          </div>
          <h1 className={auth.title}>Request cancelled</h1>
          <div className={styles.heroSub}>
            {state.clientName} was not given access. You can close this tab.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={auth.page}>
      <div className={`${auth.card} ${auth.hero}`}>
        <div className={`${auth.heroIcon} ${auth.heroIconOk}`}>
          <Check size={30} aria-hidden="true" />
        </div>
        <h1 className={auth.title}>{state.clientName} is connected</h1>
        <div className={styles.heroSub}>
          You can close this tab and return to {state.clientName}.
        </div>
        <div className={`${auth.infoBox} ${styles.infoBox}`}>
          <div>
            Manage or revoke this connection in{" "}
            <Link to="/mcp-access" className={auth.link}>
              MCP access
            </Link>
            .
          </div>
        </div>
      </div>
    </div>
  );
}
